import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, ChevronUp, ChevronDown, Save, Loader2 } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";

/**
 * AdminSbtHeroSlider — CRUD for the home-page hero slider slides.
 * Data lives in `sbt_site_slider` (title, subtitle, image_url,
 * cta_label, cta_href, position, active).
 *
 * Reuses the same pattern as AdminSbtSponsors: dirty rows are batched
 * on Save All so admins can edit multiple slides then persist in one go.
 */
export default function AdminSbtHeroSlider() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/sbt-site/slider");
      setRows((r.data.slides || []).map((s) => ({ ...s, _dirty: false })));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load slider");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const setField = (id, k, v) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [k]: v, _dirty: true } : r)));

  const move = (idx, dir) => setRows((rs) => {
    const t = idx + dir;
    if (t < 0 || t >= rs.length) return rs;
    const copy = [...rs];
    [copy[idx], copy[t]] = [copy[t], copy[idx]];
    return copy.map((r, i) => ({ ...r, position: i + 1, _dirty: true }));
  });

  const addRow = async () => {
    try {
      const r = await api.post("/admin/sbt-site/slider", {
        title: "New slide headline",
        subtitle: "",
        image_url: "",
        cta_label: "Book a Strategy Call →",
        cta_href: "/sbt-site/contact/",
        position: 0,
        active: true,
      });
      await load();
      toast.success("Slide added — edit content then Save All.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Add failed"); }
  };

  const del = async (id, title) => {
    if (!window.confirm(`Delete slide "${title || 'untitled'}"?`)) return;
    try {
      await api.delete(`/admin/sbt-site/slider/${id}`);
      setRows((rs) => rs.filter((r) => r.id !== id));
      toast.success("Slide deleted");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"); }
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      // Reorder first (positions may have shifted)
      const order = rows.map((r) => r.id);
      await api.put("/admin/sbt-site/slider/reorder/all", { order });
      // Persist edited rows
      const dirty = rows.filter((r) => r._dirty);
      for (const r of dirty) {
        await api.put(`/admin/sbt-site/slider/${r.id}`, {
          title: r.title || "",
          subtitle: r.subtitle || "",
          image_url: r.image_url || "",
          cta_label: r.cta_label || "",
          cta_href: r.cta_href || "",
          position: r.position,
          active: !!r.active,
        });
      }
      toast.success(`Saved ${dirty.length} slide(s). Reload the marketing site to see changes.`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-4 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2"/>Loading slides…</div>;

  return (
    <section data-testid="admin-hero-slider">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Hero Slider</h3>
          <p className="text-xs text-slate-500 mt-0.5">Add / edit / delete / reorder the slides shown at the top of the marketing home page. Text supports HTML; background image is optional (dark cyan pattern falls back).</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={addRow} data-testid="hero-add-btn">
            <Plus className="w-3.5 h-3.5 mr-1"/> Add Slide
          </Button>
          <Button size="sm" onClick={saveAll} disabled={saving} data-testid="hero-save-all-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/> : <Save className="w-3.5 h-3.5 mr-1"/>}
            Save All
          </Button>
        </div>
      </div>

      {rows.length === 0 && (
        <div className="border border-dashed border-slate-300 rounded p-8 text-center text-sm text-slate-500">
          No slides yet. Click <span className="font-semibold">Add Slide</span> to create the first one.
        </div>
      )}

      <div className="space-y-4">
        {rows.map((r, idx) => (
          <div key={r.id} className="border rounded-lg p-4 bg-white" data-testid={`hero-row-${idx}`}>
            <div className="flex items-start gap-3">
              <div className="flex flex-col items-center gap-1 pt-2">
                <span className="text-xs font-semibold text-slate-500 w-6 text-center">{idx + 1}</span>
                <button type="button" className="text-slate-400 hover:text-slate-700" onClick={() => move(idx, -1)}><ChevronUp className="w-4 h-4"/></button>
                <button type="button" className="text-slate-400 hover:text-slate-700" onClick={() => move(idx, 1)}><ChevronDown className="w-4 h-4"/></button>
              </div>

              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-[11px] uppercase tracking-wider text-slate-500">Background Image</Label>
                  <ImageUploader
                    value={r.image_url || ""}
                    onChange={(url) => setField(r.id, "image_url", url)}
                    category="hero-slider"
                    label="Slide background"
                    preview="wide"
                    testId={`hero-img-${idx}`}
                  />
                </div>

                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-slate-500">Headline (HTML allowed)</Label>
                  <Input
                    value={r.title || ""}
                    onChange={(e) => setField(r.id, "title", e.target.value)}
                    placeholder="From buying signals to <span class='accent'>validated meetings</span>."
                    className="text-sm"
                    data-testid={`hero-title-${idx}`}
                  />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-slate-500">Eyebrow / Pre-title</Label>
                  <Input
                    value={r.subtitle || ""}
                    onChange={(e) => setField(r.id, "subtitle", e.target.value)}
                    placeholder="Leading Sales Acceleration Strategist"
                    className="text-sm"
                    data-testid={`hero-sub-${idx}`}
                  />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-slate-500">CTA Label</Label>
                  <Input
                    value={r.cta_label || ""}
                    onChange={(e) => setField(r.id, "cta_label", e.target.value)}
                    placeholder="Book a Strategy Call →"
                    className="text-sm"
                    data-testid={`hero-cta-label-${idx}`}
                  />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-slate-500">CTA Link</Label>
                  <Input
                    value={r.cta_href || ""}
                    onChange={(e) => setField(r.id, "cta_href", e.target.value)}
                    placeholder="/sbt-site/contact/"
                    className="text-sm"
                    data-testid={`hero-cta-href-${idx}`}
                  />
                </div>
              </div>

              <div className="flex flex-col items-end gap-3 pt-2 min-w-[120px]">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  Live
                  <Switch
                    checked={!!r.active}
                    onCheckedChange={(v) => setField(r.id, "active", v)}
                    data-testid={`hero-active-${idx}`}
                  />
                </div>
                <button type="button" className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1" onClick={() => del(r.id, r.title)}>
                  <Trash2 className="w-3.5 h-3.5"/> Remove
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
